import type { DBAccount } from "./db/DBAccount.js";
import type { HumanID, ObjectID, Reason, Resource, Rules, Text, Version, Count } from "./Types.js";

/**
 * The enum of event types.
 */
export enum Type
{
    GAME_START,
    // _ > A > B

    PLAYER_JOIN,
    // ABCDF > B > BCDEFG

    CHAT,
    // BCDFGHIJK > C > BCDEFGIJK

    PLAYER_LEAVE,
    // BCDFGHIJK > D > BCDEFGIJK

    GAME_END,
    // BCDFGHIJK > E > _

    RULE_CHANGE,
    // BCDF > F > BCDEFG

    ROUND_START,
    // BCDFK > G > CDEH

    HEADLINE_CREATE,
    // G > H > CDEI

    PHOTO_SUBMIT,
    // CDIH > I > CDEIJ

    VOTE_START,
    // CDI > J > CDEK

    VOTE_RESULT,

    // CDJ > K > CDEG
    ROUND_SKIP,
}

/**
 * The event map.
 * This is a mapping of event types to their data types. (the data type is the second element in the tuple)
 * This is used to type the event data in the `Event` type.
 */
export interface EventMap extends Record<Type, unknown>
{
    [Type.GAME_START]: [
        issue: Count,
        serverVersionInfo: Version,
    ],
    [Type.PLAYER_JOIN]: [
        clientVersionInfo: Version,
        player: HumanID,
        name: Text,
        icon: Resource,
        theme: Resource,
        profileData: Partial<DBAccount>|null,
    ],
    [Type.CHAT]: [
        sender: HumanID,
        message: Text,
    ],
    [Type.PLAYER_LEAVE]: [
        player: HumanID,
        host: HumanID,
        reason: Reason,
    ],
    [Type.GAME_END]: [
        reason: Reason,
    ],
    [Type.RULE_CHANGE]: [
        changes: Rules,
    ],
    [Type.ROUND_START]: [
        round: Count,
        editor: HumanID,
    ],
    [Type.HEADLINE_CREATE]: [
        round: Count,
        headline: Text,
    ],
    [Type.PHOTO_SUBMIT]: [
        round: Count,
        player: HumanID,
        photo: Resource,
    ],
    [Type.VOTE_START]: [
        round: Count,
    ],
    [Type.VOTE_RESULT]: [
        round: Count,
        winner: HumanID,
    ],
    [Type.ROUND_SKIP]:
    [
        round: Count,
        reason: Reason
    ]
}

/**
 * The raw event type.
 */
export type RawEvent = [
    type: Type,
    id: ObjectID,
    createdAt: Date,
    ...data: unknown[],
];

/**
 * Parse the event data.
 * @param event The event type.
 * @param input The raw event data.
 * @returns The parsed event data.
 */
export const parse = <EventType extends Type>(
    type: EventType,
    data: RawEvent
) =>
{
    const [_type, id, createdAt, ...eventData] = data;

    return {
        type,
        id,
        time: new Date(createdAt.toString()),
        data: eventData as EventMap[EventType],
    };
};

/**
 * The event type.
 */
export type Event<EventType extends Type> = ReturnType<typeof parse<EventType>>;

export const serialize = <EventType extends Type>(event: Event<EventType>): string =>
{
    const rawEvent: RawEvent = [
        event.type,
        event.id,
        event.time,
        ...event.data,
    ];

    return JSON.stringify(rawEvent);
};
